// Read-only catalogue access with request de-duplication and a short-lived
// in-memory cache, plus helpers that map the product API model to UI values.
// Cart / order / account calls deliberately stay on service.js uncached.
import * as service from "./service";
import * as endpoint from "./endpoints";

const cache = new Map();
const MINUTE = 60 * 1000;

function cached(key, loader, ttl = 5 * MINUTE) {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.time < ttl) return hit.promise;
  const promise = loader().then(
    (response) => response.data,
    (error) => {
      cache.delete(key);
      throw error;
    }
  );
  cache.set(key, { promise, time: Date.now() });
  return promise;
}

const asList = (data) => (Array.isArray(data) ? data : []);

export const getCategories = () =>
  cached("categories", service.getAllCategories, 30 * MINUTE).then(asList);

export const getSubCategories = (category) =>
  cached(`sub:${category}`, () => service.getSubCategories(category), 30 * MINUTE).then(asList);

export const getProductsByCategory = (category) =>
  cached(`cat:${category}`, () => service.getProductByCategory(category)).then(asList);

export const getTrending = () => cached("trending", service.getTrendingProducts).then(asList);

export const getPopular = () => cached("popular", service.getPopularProducts).then(asList);

export const searchProducts = (query) => {
  const q = query.trim();
  if (!q) return Promise.resolve([]);
  return cached(
    `search:${q.toLowerCase()}`,
    () => service.searchItems(encodeURIComponent(q))
  ).then(asList);
};

/* ---------- Product model helpers ---------- */

const GST = 1.18;
const inr = typeof Intl !== "undefined" ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }) : null;

export function formatINR(value) {
  const n = Math.round(Number(value) || 0);
  return `₹${inr ? inr.format(n) : n}`;
}

// Prices from the API include GST; the storefront has always displayed them ex-GST.
export function exGst(value) {
  return Math.round((Number(value) || 0) / GST);
}

export function categorySlug(product) {
  return (product && product.category && product.category.slug) || "";
}

export function displayPrice(product) {
  if (!product || product.isMedical === true || !product.startingFrom) return null;
  return exGst(product.startingFrom);
}

export function priceUnit(product) {
  return categorySlug(product) === "seasonal" ? "season" : "month";
}

export function minTenureLabel(product) {
  if (!product) return null;
  if (product.isMedical === true) return "1 month";
  const slug = categorySlug(product);
  if (slug === "seasonal") return null;
  if (slug === "premium") return "12 months";
  return "6 months";
}

export function availability(product) {
  const status = product && product.status;
  if (status === "Out of Stock") return "out";
  if (status === "Available") return "in";
  return null;
}

export function productPath(product) {
  if (!product || !product.category || !product.subCategory) return null;
  return endpoint.getPath(`${product.category.slug}/${product.subCategory.slug}/${product.slug}`);
}

export function isExpressCity() {
  return endpoint.getCurrentCity() === "gurgaon";
}

// Picks the single most useful badge for a product card, from real flags only.
export function productBadge(product) {
  if (!product) return null;
  if (product.instantDelivery && isExpressCity()) return { label: "Express delivery", tone: "brand" };
  if (product.isTrending) return { label: "Trending", tone: "accent" };
  if (product.isPopular) return { label: "Popular", tone: "accent" };
  if (product.infoMessage) {
    const text = String(product.infoMessage).trim().toLowerCase();
    return text ? { label: text.charAt(0).toUpperCase() + text.slice(1), tone: "solid" } : null;
  }
  return null;
}

export function titleCase(text) {
  return String(text || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
